/**
 * Data layer for `click-spam-detection`. Thin typed fetch wrappers over /api/v1/click-spam-detection.
 * Raw `fetch()` is confined to this file per the dash contract.
 */

export interface ClickSpamDetectionRow {
	id: number;
	tenant_id: string;
	event_id: string;
	click_id: string;
	ip: string;
	user_agent: string;
	signal: string;
	severity: string;
	score: number;
	flagged: boolean;
	metadata: unknown;
	created_at: number;
}

export interface ListClickSpamDetectionResponse {
	items: ClickSpamDetectionRow[];
}

export interface ClickSpamDetectionStats {
	total: number;
	flagged: number;
	by_signal: Record<string, number>;
	by_severity: Record<string, number>;
}

export interface ListClickSpamDetectionFilters {
	signal?: string;
	severity?: string;
	flagged?: "1" | "0";
	since?: number;
	until?: number;
	limit?: number;
}

export interface CreateClickSpamDetectionBody {
	event_id: string;
	click_id: string;
	ip: string;
	user_agent: string;
	signal: string;
	severity?: string;
	score?: number;
	flagged?: boolean;
	metadata?: Record<string, unknown>;
}

const BASE = "/api/v1/click-spam-detection";

async function parseOrThrow<T>(res: Response): Promise<T> {
	const text = await res.text();
	let data: unknown = null;
	if (text) {
		try {
			data = JSON.parse(text);
		} catch {
			data = text;
		}
	}
	if (!res.ok) {
		const msg =
			data &&
			typeof data === "object" &&
			data !== null &&
			"error" in data &&
			typeof (data as { error: unknown }).error === "string"
				? (data as { error: string }).error
				: `HTTP ${res.status}`;
		throw new Error(msg);
	}
	return data as T;
}

export async function list(
	filters: ListClickSpamDetectionFilters = {},
): Promise<ListClickSpamDetectionResponse> {
	const qs = new URLSearchParams();
	if (filters.signal) qs.set("signal", filters.signal);
	if (filters.severity) qs.set("severity", filters.severity);
	if (filters.flagged !== undefined) qs.set("flagged", filters.flagged);
	if (filters.since !== undefined) qs.set("since", String(filters.since));
	if (filters.until !== undefined) qs.set("until", String(filters.until));
	if (filters.limit !== undefined) qs.set("limit", String(filters.limit));
	const url = qs.toString() ? `${BASE}?${qs.toString()}` : BASE;
	const res = await fetch(url, {
		method: "GET",
		credentials: "include",
		headers: { accept: "application/json" },
	});
	return parseOrThrow<ListClickSpamDetectionResponse>(res);
}

export async function stats(): Promise<ClickSpamDetectionStats> {
	const res = await fetch(`${BASE}/stats`, {
		method: "GET",
		credentials: "include",
		headers: { accept: "application/json" },
	});
	return parseOrThrow<ClickSpamDetectionStats>(res);
}

export async function create(
	body: CreateClickSpamDetectionBody,
): Promise<{ ok: boolean; event_id: string; flagged: boolean; created_at: number }> {
	const res = await fetch(BASE, {
		method: "POST",
		credentials: "include",
		headers: {
			accept: "application/json",
			"content-type": "application/json",
		},
		body: JSON.stringify(body),
	});
	return parseOrThrow<{ ok: boolean; event_id: string; flagged: boolean; created_at: number }>(res);
}

export const clickSpamDetectionQueryKeys = {
	all: ["click-spam-detection"] as const,
	list: (filters: ListClickSpamDetectionFilters = {}) =>
		[...clickSpamDetectionQueryKeys.all, "list", filters] as const,
	stats: () => [...clickSpamDetectionQueryKeys.all, "stats"] as const,
	detail: (id: string) => [...clickSpamDetectionQueryKeys.all, id] as const,
};
